import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Loader2, Plus, Phone, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Skeleton } from '../../../components/ui/skeleton';
import { toast } from 'sonner';
import { DBAllocatedPhoneNumberThunks } from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useCallback } from 'react';
import { AllocatedPhoneNumber } from '@common-models/allocated-phone-number';

const statusColors = {
  requested: 'bg-yellow-100 text-yellow-800',
  pending_approval: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800'
};

const whatsappStatusColors = {
  not_requested: 'bg-gray-100 text-gray-800',
  pending: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
};

export const AllocatedPhoneNumbers: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [friendlyName, setFriendlyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Access the Redux state directly
  const phoneNumbersState = useAppSelector(state => state.DBAllocatedPhoneNumberReducer);
  // Get the entities from the state and convert to array
  const phoneNumbers = phoneNumbersState && phoneNumbersState.ids 
    ? phoneNumbersState.ids.map(id => phoneNumbersState.entities[id]).filter(Boolean) as AllocatedPhoneNumber[]
    : [];

  // Fetch phone numbers when component mounts
  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      setIsLoading(true);
      try {
        await dispatch(DBAllocatedPhoneNumberThunks.find({}));
      } catch (error) {
        console.error('Error fetching phone numbers:', error);
        toast.error('Failed to load phone numbers');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhoneNumbers();
  }, [dispatch]);

  // Refresh phone numbers
  const refetch = useCallback(() => {
    setIsLoading(true);
    dispatch(DBAllocatedPhoneNumberThunks.find({}))
      .then(() => {
        toast.success('Phone numbers refreshed');
      })
      .catch(error => {
        console.error('Error refreshing phone numbers:', error);
        toast.error('Failed to refresh phone numbers');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  const handleRequestNumber = async () => {
    if (!friendlyName.trim()) {
      toast.error('Name required', {
        description: 'Please enter a friendly name for this phone number.'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(DBAllocatedPhoneNumberThunks.create({ 
        friendlyName,
        status: 'requested',
        whatsappStatus: 'not_requested', 
        whatsappEnabled: false,
        phoneNumber: 'pending_allocation',
      })).unwrap();
      
      setIsRequestDialogOpen(false);
      setFriendlyName('');
      // Refresh the data
      dispatch(DBAllocatedPhoneNumberThunks.find({}));
      toast.success('Request submitted', {
        description: 'Your phone number request has been submitted and is awaiting approval.'
      });
    } catch (error) {
      toast.error('Request failed', {
        description: 'Failed to submit phone number request. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPhoneNumber = (number: string) => {
    if (number === 'pending_allocation') {
      return <span className="italic text-gray-500">Pending allocation</span>;
    }
    return number;
  };

  const getStatusBadge = (status: string) => {
    const colorClass = statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800';
    return <Badge className={colorClass}>{status.replace('_', ' ')}</Badge>;
  };

  const getWhatsAppStatusBadge = (status: string) => {
    const colorClass = whatsappStatusColors[status as keyof typeof whatsappStatusColors] || 'bg-gray-100 text-gray-800';
    return <Badge className={colorClass}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>WhatsApp Phone Numbers</span>
          <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Request Number
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Request a WhatsApp Number</DialogTitle>
                <DialogDescription>
                  Request a new phone number that can be used to receive WhatsApp messages.
                  Once approved, customers can send messages to this number.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Friendly Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Customer Support, Sales, etc."
                    value={friendlyName}
                    onChange={(e) => setFriendlyName(e.target.value)}
                  />
                </div>
                <div className="bg-amber-50 p-3 rounded border border-amber-200 text-amber-800 text-sm">
                  <AlertCircle className="h-4 w-4 inline-block mr-2" />
                  Note: Phone numbers require approval. Once your request is approved, 
                  our team will allocate a phone number and configure WhatsApp for it.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleRequestNumber} disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Submit Request
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardTitle>
        <CardDescription>
          Manage phone numbers allocated for receiving WhatsApp messages from your customers.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : phoneNumbers.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>WhatsApp Status</TableHead>
                <TableHead>Requested</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {phoneNumbers.map((number) => (
                <TableRow key={number._id}>
                  <TableCell>{number.friendlyName}</TableCell>
                  <TableCell>{formatPhoneNumber(number.phoneNumber)}</TableCell>
                  <TableCell>{getStatusBadge(number.status)}</TableCell>
                  <TableCell>
                    {number.whatsappEnabled ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" /> Enabled
                      </Badge>
                    ) : (
                      getWhatsAppStatusBadge(number.whatsappStatus)
                    )}
                  </TableCell>
                  <TableCell>{number.createdAt ? new Date(number.createdAt).toLocaleDateString() : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 space-y-4">
            <Phone className="h-12 w-12 mx-auto text-gray-400" />
            <div className="space-y-2">
              <p className="text-lg font-medium">No phone numbers yet</p>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Request a WhatsApp-enabled phone number to start receiving messages from your customers.
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setIsRequestDialogOpen(true)}
              className="mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Request Your First Number
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter className="border-t pt-4 flex justify-between">
        <p className="text-sm text-gray-500">
          {phoneNumbers.length} phone number(s)
        </p>
        <Button variant="ghost" size="sm" onClick={refetch}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </CardFooter>
    </Card>
  );
}; 